import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import oneDriveService from '../services/oneDriveService';
import BulkImportProgress from './BulkImportProgress';
import { extractErrorMessage } from '../../../utils/errorUtils';
import ConnectStep from './steps/ConnectStep';
import FolderBrowserStep from './steps/FolderBrowserStep';
import ConfigureStep from './steps/ConfigureStep';
import ConfirmStep from './steps/ConfirmStep';

const STEPS = ['Conectar OneDrive', 'Seleccionar Carpeta', 'Configurar Campos', 'Confirmar'];

const OneDriveBulkImport = ({ open, onClose, companies, mode = 'new' }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // OneDrive auth
  const [accessToken, setAccessToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [driveId, setDriveId] = useState(null);

  // Folder selection
  const [currentPath, setCurrentPath] = useState('/');
  const [allItems, setAllItems] = useState([]);
  const [pathStack, setPathStack] = useState([]);
  const [browseMode, setBrowseMode] = useState('navigate');
  const [directPathInput, setDirectPathInput] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [level1Items, setLevel1Items] = useState([]);
  const [itemMode, setItemMode] = useState('folders');

  // Field mapping (solo para mode='new')
  const [folderMappings, setFolderMappings] = useState([]);
  const [mappingsValid, setMappingsValid] = useState(false);

  // Job
  const [jobId, setJobId] = useState(null);
  const [jobStarted, setJobStarted] = useState(false);
  const [authUrl, setAuthUrl] = useState('');

  useEffect(() => {
    if (open) {
      resetWizard();
      oneDriveService.initiateAuth()
        .then(res => setAuthUrl(res.data.authUrl))
        .catch(() => {});
    }
  }, [open]);

  const resetWizard = () => {
    setActiveStep(0);
    setAccessToken('');
    setRefreshToken('');
    setDriveId(null);
    setAuthUrl('');
    setCurrentPath('/');
    setAllItems([]);
    setPathStack([]);
    setBrowseMode('navigate');
    setDirectPathInput('');
    setSelectedFolder(null);
    setLevel1Items([]);
    setItemMode('folders');
    setFolderMappings([]);
    setMappingsValid(false);
    setJobId(null);
    setJobStarted(false);
    setError('');
  };

  // ── Auth ──────────────────────────────────────────────────────────────────

  const handleConnectOneDrive = () => {
    if (!authUrl) {
      setError('Cargando URL de autenticación, intenta de nuevo en un momento');
      return;
    }
    setError('');
    const authWindow = window.open(authUrl, 'OneDrive Auth', 'width=600,height=700');
    const channel = new BroadcastChannel('onedrive-auth');
    channel.onmessage = (event) => {
      channel.close();
      if (event.data.type === 'onedrive-auth-success') {
        const { accessToken: newToken, refreshToken: newRefresh } = event.data;
        setAccessToken(newToken);
        setRefreshToken(newRefresh);
        if (authWindow && !authWindow.closed) authWindow.close();
        setActiveStep(1);
        loadDirectory('/', newToken);
      } else if (event.data.type === 'onedrive-auth-error') {
        setError(event.data.error || 'Error al autenticar con OneDrive');
      }
    };
  };

  // ── Directory navigation ──────────────────────────────────────────────────

  const loadDirectory = async (path, token, resolvedDriveId) => {
    try {
      setLoading(true);
      setError('');
      const tokenToUse = token || accessToken;
      const useDriveId = resolvedDriveId !== undefined ? resolvedDriveId : driveId;
      const response = await oneDriveService.getFolderContents(tokenToUse, path, false, useDriveId);
      const items = response.data || [];
      setAllItems(items);
      setCurrentPath(path);
    } catch {
      setError('Error al cargar carpeta de OneDrive');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateInto = (folder) => {
    const name = currentPath === '/' ? 'Inicio' : currentPath.split('/').filter(Boolean).pop() || 'Inicio';
    setPathStack(prev => [...prev, { path: currentPath, name }]);
    loadDirectory(folder.path);
  };

  const handleBreadcrumbNavigate = (index) => {
    const target = index === -1 ? { path: '/', name: 'Inicio' } : pathStack[index];
    setPathStack(prev => prev.slice(0, index === -1 ? 0 : index));
    loadDirectory(target.path);
  };

  // ── Folder selection ──────────────────────────────────────────────────────

  const handleSelectFolder = async (folder, resolvedDriveId) => {
    setSelectedFolder(folder);
    if (mode === 'new') {
      try {
        setLoading(true);
        const useDriveId = resolvedDriveId !== undefined ? resolvedDriveId : driveId;
        const response = await oneDriveService.getFolderContents(accessToken, folder.path, false, useDriveId);
        const allItms = response.data;
        const subFolders = allItms.filter(item => item.isFolder);
        const files = allItms.filter(item => !item.isFolder);
        if (subFolders.length > 0) {
          setItemMode('folders');
          setLevel1Items(subFolders);
        } else {
          setItemMode('files');
          setLevel1Items(files);
        }
      } catch {
        setError('Error al cargar contenido de la carpeta');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSelectCurrentFolder = () => {
    const name = currentPath === '/' ? 'Raíz' : currentPath.split('/').filter(Boolean).pop() || currentPath;
    handleSelectFolder({ id: currentPath, path: currentPath, name });
  };

  const handleUseDirectPath = async () => {
    let path = directPathInput.trim();
    if (!path) return;

    let resolvedDriveId = null;

    if (path.startsWith('https://') || path.startsWith('http://')) {
      try {
        const url = new URL(path);
        const idParam = url.searchParams.get('id');
        if (!idParam) {
          setError('No se pudo extraer la ruta de la URL. Ingresa la ruta directamente (ej: /MiCarpeta)');
          return;
        }

        const parts = idParam.split('/').filter(Boolean);
        const personalIndex = parts.findIndex(p => p.toLowerCase() === 'personal');
        const docsIndex = parts.findIndex(p => p.toLowerCase() === 'documents');

        if (docsIndex !== -1) {
          const remaining = parts.slice(docsIndex + 1);
          path = remaining.length > 0 ? '/' + remaining.join('/') : '/';
        } else if (parts.length > 2) {
          path = '/' + parts.slice(2).join('/');
        } else {
          setError('No se pudo interpretar la ruta de la URL de SharePoint');
          return;
        }

        if (personalIndex !== -1 && parts[personalIndex + 1]) {
          const hostname = url.hostname;
          const sitePath = `/personal/${parts[personalIndex + 1]}`;
          try {
            setLoading(true);
            const result = await oneDriveService.resolveSharePointUrl(accessToken, hostname, sitePath);
            resolvedDriveId = result.data?.driveId || null;
            setDriveId(resolvedDriveId);
          } catch {
            setDriveId(null);
          } finally {
            setLoading(false);
          }
        }
      } catch {
        setError('URL inválida. Ingresa la ruta directamente (ej: /MiCarpeta)');
        return;
      }
    }

    const name = path.split('/').filter(Boolean).pop() || path;
    handleSelectFolder({ id: path, path, name }, resolvedDriveId);
  };

  // ── Wizard navigation ─────────────────────────────────────────────────────

  const handleNext = () => {
    if (activeStep === 1 && !selectedFolder) {
      setError('Selecciona una carpeta');
      return;
    }
    if (activeStep === 2 && mode === 'new' && !mappingsValid) {
      setError('Completa la configuración de todos los expedientes');
      return;
    }
    setError('');
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep(prev => prev - 1);
  };

  // ── Import ────────────────────────────────────────────────────────────────

  const handleStartImport = async () => {
    try {
      setLoading(true);
      setError('');
      const importData = {
        oneDriveRootFolder: selectedFolder.path,
        accessToken,
        refreshToken,
        ...(driveId && { driveId }),
      };
      let response;
      if (mode === 'new') {
        importData.folderMappings = folderMappings;
        response = await oneDriveService.startNewProceedingsImport(importData);
      } else {
        response = await oneDriveService.startExistingProceedingsImport(importData);
      }
      setJobId(response.data.id);
      setJobStarted(true);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAfterStart = () => {
    onClose();
    resetWizard();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const stepContent = [
    <ConnectStep loading={loading} onConnect={handleConnectOneDrive} />,
    <FolderBrowserStep
      loading={loading}
      allItems={allItems}
      pathStack={pathStack}
      selectedFolder={selectedFolder}
      level1Items={level1Items}
      itemMode={itemMode}
      browseMode={browseMode}
      directPathInput={directPathInput}
      mode={mode}
      onBrowseModeChange={setBrowseMode}
      onNavigateInto={handleNavigateInto}
      onBreadcrumbNavigate={handleBreadcrumbNavigate}
      onSelectFolder={handleSelectFolder}
      onSelectCurrentFolder={handleSelectCurrentFolder}
      onDirectPathInputChange={setDirectPathInput}
      onUseDirectPath={handleUseDirectPath}
      onClearSelectedFolder={() => setSelectedFolder(null)}
    />,
    <ConfigureStep
      mode={mode}
      level1Items={level1Items}
      itemMode={itemMode}
      companies={companies}
      onMappingsChange={setFolderMappings}
      onValidChange={setMappingsValid}
    />,
    <ConfirmStep selectedFolder={selectedFolder} mode={mode} folderMappings={folderMappings} />,
  ];

  if (jobStarted) {
    return (
      <Dialog open={open} onClose={handleCloseAfterStart} maxWidth="md" fullWidth>
        <DialogTitle>Importación en Progreso</DialogTitle>
        <DialogContent>
          <BulkImportProgress jobId={jobId} onClose={handleCloseAfterStart} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {mode === 'new' ? 'Importar Nuevos Expedientes desde OneDrive' : 'Agregar Documentos desde OneDrive'}
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {stepContent[activeStep]}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        {activeStep > 0 && activeStep < 3 && (
          <Button onClick={handleBack}>Atrás</Button>
        )}
        {activeStep > 0 && activeStep < 3 && (
          <Button variant="contained" onClick={handleNext}>
            Siguiente
          </Button>
        )}
        {activeStep === 3 && (
          <Button
            variant="contained"
            onClick={handleStartImport}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {loading ? 'Iniciando...' : 'Iniciar Importación'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OneDriveBulkImport;
